import { useQuery } from '@apollo/client';
import {
  EEntityOwnership,
  EFilterType,
  TEntityOwnership,
  TFilter,
  TFilterEvent,
} from 'Components/Filter/filter.types';
import useDialog from 'hooks/useDialog';
import { useLogEvent } from 'hooks/useLogEvent';
import { ACTIVITIES_TABLE_ROWS_PER_PAGE } from 'utils/pagination.utils';
import { GET_ACTIVITIES } from 'graphql/secure/queries/activity.queries';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IActivity } from 'FeatureModules/Activity/types/activity.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { ISite } from 'FeatureModules/Site/types/site.types';
import { IGraphQlConnection } from 'types/core.types';
import { createEnumParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

interface IUseActivitiesProps {
  // Can be used to skip the useQueryParam search and use the internal search term instead
  skipUseQueryParams?: boolean;
  defaultOwnedById?: string;
  defaultSiteId?: string;
  additionalAddEditDialogProps?: {
    /**
     * The site selector will be preselected and disabled
     */
    site?: ISite;
    /**
     * The partner / company selector will be hidden and the partner can be preselected with the partner prop
     */
    partner?: IBasePartnerCompany;
    hidePartnerSelector?: boolean;
  };
}

interface IQueryState {
  searchTerm?: string | null;
  owner?: TEntityOwnership | null;
  ownedById?: string | null;
}

const useActivities = ({
  defaultOwnedById,
  skipUseQueryParams,
  defaultSiteId,
  additionalAddEditDialogProps,
}: IUseActivitiesProps = {}) => {
  const { openDialog } = useDialog();
  const { logEvent } = useLogEvent();
  const navigate = useNavigate();

  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, null),
      owner: withDefault(
        createEnumParam<TEntityOwnership>(Object.values(EEntityOwnership)),
        defaultOwnedById ? EEntityOwnership.PARTNER : null
      ),
      ownedById: withDefault(StringParam, defaultOwnedById),
    },
    { removeDefaultsFromUrl: true }
  );

  const [internalQueryState, setInternalQueryState] = useState<IQueryState>({
    searchTerm: '',
    owner: defaultOwnedById ? EEntityOwnership.PARTNER : null,
    ownedById: defaultOwnedById,
  });

  const { searchTerm, owner, ownedById } = skipUseQueryParams ? internalQueryState : query;

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.OWNERSHIP,
        value: owner
          ? {
              option: owner,
              ownedById,
            }
          : undefined,
      },
    ],
    [owner, ownedById]
  );

  const { data, loading, error, fetchMore } = useQuery<{
    activities: IGraphQlConnection<IActivity>;
  }>(GET_ACTIVITIES, {
    // when using this inside the partner activity page, we need to pass the companyId to get the activities of that partner
    variables: {
      filters: {
        ...(searchTerm ? { searchTerm } : undefined),
        ...(ownedById && {
          ownedById,
        }),
        ...(defaultSiteId && { siteId: defaultSiteId }),
        ...(owner && owner !== EEntityOwnership.PARTNER && { ownershipType: owner }),
      },
      first: ACTIVITIES_TABLE_ROWS_PER_PAGE,
    },
  });

  const hasNextPage: boolean = !!data?.activities?.pageInfo?.hasNextPage;
  const endCursor: string | undefined = data?.activities?.pageInfo?.endCursor;

  const activities = useMemo(
    () => (data?.activities ? removeGraphConnections(data.activities) : []),
    [data?.activities]
  );

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleCreateActivity = () =>
    openDialog({
      type: 'ADD_EDIT_ACTIVITY',
      props: {
        mode: 'add-activity',
        ...additionalAddEditDialogProps,
      },
    });

  const handleEditActivity = (activity: IActivity) => {
    openDialog({
      type: 'ADD_EDIT_ACTIVITY',
      props: {
        mode: 'edit-activity',
        activity,
        ...additionalAddEditDialogProps,
      },
    });
  };

  const handleSearchTermChange = (searchTerm: string) => {
    logEvent('ACTIVITY_SEARCHED', { searchTerm });
    skipUseQueryParams
      ? setInternalQueryState(prev => ({ ...prev, searchTerm }))
      : setQuery(prev => ({ ...prev, searchTerm }));
  };

  const handleFilterChange = (event: TFilterEvent) => {
    if (event.type === EFilterType.OWNERSHIP) {
      skipUseQueryParams
        ? setInternalQueryState(prev => ({
            ...prev,
            owner: event.value?.option || undefined,
            ownedById: event.value?.ownedById || undefined,
          }))
        : setQuery(prev => ({
            ...prev,
            owner: event.value?.option || undefined,
            ownedById: event.value?.ownedById || undefined,
          }));
    }
  };

  const handleFilterReset = () => {
    skipUseQueryParams
      ? setInternalQueryState(prev => ({
          ...prev,
          owner: undefined,
          ownedById: undefined,
        }))
      : setQuery(prev => ({
          owner: undefined,
          ownedById: undefined,
        }));
  };

  const handleSelectActivity = (activity: IActivity) => navigate(`/activities/${activity.id}`);

  return {
    hasNextPage,
    activities,
    loading,
    error,
    searchTerm,
    filters,
    handleSearchTermChange,
    handleCreateActivity,
    handleEditActivity,
    handleFilterChange,
    handleFilterReset,
    handlePageEndReached,
    handleSelectActivity,
  };
};

export default useActivities;
